import { useMemo, useState } from 'react';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useManualReferralEntitySearch } from '../../hooks/useManualReferralEntitySearch';
import type {
  ManualReferralEntitySearchItem,
  ManualReferralRole,
} from '../../types/manual-referral.type';

type ManualReferralEntitySelectProps = {
  label: string;
  searchLabel: string;
  entityType?: ManualReferralRole;
  selected: ManualReferralEntitySearchItem | null;
  onSelect: (entity: ManualReferralEntitySearchItem) => void;
  allowedRoles?: ManualReferralRole[];
  /** Feature 127: hide provisional contacts — only a real account can be linked or can invite. */
  usersOnly?: boolean;
  placeholder?: string;
};

export const ManualReferralEntitySelect = ({
  label,
  searchLabel,
  entityType,
  selected,
  onSelect,
  allowedRoles,
  usersOnly = false,
  placeholder = 'Search by name or email',
}: ManualReferralEntitySelectProps) => {
  const [query, setQuery] = useState('');
  const { data, isFetching } = useManualReferralEntitySearch(query, entityType);
  const canShowResults = query.trim().length >= 2;

  const items = useMemo(() => {
    if (!canShowResults) return [];
    const results = (data?.items ?? []).filter((item) => !usersOnly || item.kind === 'USER');
    if (!allowedRoles) return results;
    return results.filter((item) => allowedRoles.includes(item.role));
  }, [allowedRoles, canShowResults, data?.items, usersOnly]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {selected && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
          <p className="font-medium text-gray-900">
            {selected.label}
            {selected.kind === 'CONTACT' && <NotOnPlatformBadge />}
          </p>
          {selected.email && <p className="text-gray-600">{selected.email}</p>}
          {selected.organizationName && (
            <p className="text-gray-600">{selected.organizationName}</p>
          )}
          <AdvisorLine advisors={selected.advisors} />
        </div>
      )}
      <div>
        <Label htmlFor={`${searchLabel.replace(/\s+/g, '-').toLowerCase()}-input`} className="sr-only">
          {searchLabel}
        </Label>
        <Input
          id={`${searchLabel.replace(/\s+/g, '-').toLowerCase()}-input`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />
      </div>
      {query.trim().length > 0 && query.trim().length < 2 && (
        <p className="text-xs text-gray-500">Enter at least 2 characters.</p>
      )}
      {isFetching && <p className="text-sm text-gray-500">Searching...</p>}
      {items.length > 0 && (
        <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-200 bg-white p-1">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onSelect(item);
                setQuery('');
              }}
              className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#026857]"
            >
              <span className="block font-medium text-gray-900">
                {item.label}
                {item.kind === 'CONTACT' && <NotOnPlatformBadge />}
              </span>
              <span className="block text-gray-600">
                {[item.organizationName, item.email].filter(Boolean).join(' - ')}
              </span>
              <AdvisorLine advisors={item.advisors} className="block text-xs text-gray-500" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Feature 101: provisional contacts (not yet on the platform) are re-usable
// from search and must be visibly distinct from real accounts.
const NotOnPlatformBadge = () => (
  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
    Not on platform
  </span>
);

// Feature 127: a care seeker's advisor is the detail that tells two people with the same name
// apart. Only care-seeker hits carry the key; an empty list is worth saying out loud.
const AdvisorLine = ({
  advisors,
  className = 'text-gray-600',
}: {
  advisors?: ManualReferralEntitySearchItem['advisors'];
  className?: string;
}) => {
  if (!advisors) return null;
  return (
    <span className={className}>
      {advisors.length === 0
        ? 'No advisor on file'
        : `Advisor: ${advisors
            .map((advisor) => (advisor.firm ? `${advisor.name} (${advisor.firm})` : advisor.name))
            .join(', ')}`}
    </span>
  );
};
